<template>
  <section class="finale" :class="{ 'is-dealing': shouldDeal }" aria-labelledby="finale-title">
    <p class="finale-meta">Final score after {{ rounds.length }} rounds</p>

    <div class="winners" :style="{ '--count': winners.length }" aria-hidden="true">
      <div
        v-for="(winner, i) in winners"
        :key="winner.id"
        class="deal"
        :style="{
          '--tilt': `${(i - (winners.length - 1) / 2) * 7 - (winners.length === 1 ? 3 : 0)}deg`,
          '--delay': `${i * 140}ms`
        }"
      >
        <div class="playing-card">
          <div class="face front">
            <span class="corner top"><span>A</span><span class="heart">♥</span></span>
            <span class="card-body">
              <span class="card-name">{{ winner.name }}</span>
              <span class="card-score" :class="{ 'is-negative': winner.total < 0 }">
                {{ formatTotal(winner.total) }}
              </span>
            </span>
            <span class="corner bottom"><span>A</span><span class="heart">♥</span></span>
          </div>
          <div class="face back"></div>
        </div>
      </div>
    </div>

    <h1 id="finale-title" class="finale-caption">{{ caption }}</h1>

    <ol v-if="others.length" class="others" aria-label="Final standings">
      <li v-for="row in others" :key="row.id" class="other">
        <span class="other-rank" :aria-label="`Place ${row.rank}`">{{ row.rank }}</span>
        <span class="other-name">{{ row.name }}</span>
        <span class="other-gap">{{ row.gap }} behind</span>
        <span class="other-total">
          {{ formatTotal(row.total) }}
        </span>
      </li>
    </ol>

    <div class="finale-actions">
      <button type="button" class="btn btn-primary" @click="emit('rematch')">
        Rematch with same players
      </button>
      <button type="button" class="btn btn-secondary sheet-link" @click="emit('view-sheet')">
        View scoresheet
      </button>
    </div>

    <button type="button" class="undo-link" :disabled="!rounds.length" @click="emit('undo')">
      Undo last round
    </button>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { formatTotal, playerTotal } from '../utils/scores';
import { getItem, setItem } from '../storage';

const CELEBRATED_KEY = 'celebratedGameId';

const props = defineProps({
  gameId: {
    type: String,
    default: null
  },
  players: {
    type: Array,
    required: true
  },
  rounds: {
    type: Array,
    required: true
  }
});

const emit = defineEmits(['rematch', 'view-sheet', 'undo']);

const rows = computed(() => {
  const totals = props.players.map((player) => playerTotal(props.rounds, player.id));
  const best = Math.max(...totals);
  return props.players
    .map((player, i) => ({
      id: player.id,
      name: player.name,
      seat: i,
      total: totals[i],
      rank: totals.filter((other) => other > totals[i]).length + 1,
      gap: best - totals[i]
    }))
    .sort((a, b) => a.rank - b.rank || a.seat - b.seat);
});

const winners = computed(() => rows.value.filter((row) => row.rank === 1));
const others = computed(() => rows.value.filter((row) => row.rank > 1));

const joinNames = (names) =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;

const caption = computed(() =>
  winners.value.length > 1
    ? `${joinNames(winners.value.map((w) => w.name))} share the win`
    : `${winners.value[0]?.name} wins`
);

const readCelebrated = () => {
  try {
    return getItem(CELEBRATED_KEY);
  } catch {
    return null;
  }
};

const shouldDeal = props.gameId !== null && readCelebrated() !== props.gameId;

const markCelebrated = () => {
  try {
    setItem(CELEBRATED_KEY, props.gameId);
  } catch {
    return;
  }
};

if (shouldDeal) markCelebrated();
</script>

<style scoped>
.finale {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1.25rem 1.25rem;
  border-radius: 14px;
  background-color: var(--panel-solid);
  border: 1px solid var(--panel-edge);
}

.finale-meta {
  color: var(--on-table-muted);
  font-size: var(--step--1);
}

.winners {
  --card-w: clamp(6.5rem, calc(76vw / var(--count)), 13.5rem);
  display: flex;
  justify-content: center;
  margin: 1.5rem 0 1.25rem;
  perspective: 900px;
}

.deal {
  width: var(--card-w);
  aspect-ratio: 5 / 7;
  transform: rotate(var(--tilt));
}

.deal + .deal {
  margin-left: calc(var(--card-w) * -0.1);
}

.playing-card {
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
}

.face {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.front {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.25rem 0.875rem;
  background-color: var(--paper);
  color: var(--card-ink);
  box-shadow:
    0 0 0 1px var(--card-line),
    0 18px 36px -14px rgba(0, 0, 0, 0.95);
}

.back {
  transform: rotateY(180deg);
  background-color: var(--strong);
  background-image:
    repeating-linear-gradient(45deg, rgba(246, 247, 249, 0.14) 0 2px, transparent 2px 9px),
    repeating-linear-gradient(-45deg, rgba(246, 247, 249, 0.14) 0 2px, transparent 2px 9px);
  box-shadow:
    inset 0 0 0 6px var(--paper),
    inset 0 0 0 7px var(--card-line),
    0 18px 36px -14px rgba(0, 0, 0, 0.95);
}

.corner {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.375rem;
  line-height: 0.95;
  color: var(--card-red);
}

.corner .heart {
  font-size: 1.0625rem;
}

.corner.top {
  top: 0.625rem;
  left: 0.625rem;
}

.corner.bottom {
  right: 0.625rem;
  bottom: 0.625rem;
  transform: rotate(180deg);
}

.card-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  max-width: 100%;
  text-align: center;
}

.card-name {
  max-width: 100%;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(1.375rem, calc(var(--card-w) / 5.2), 2.625rem);
  line-height: 0.95;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-score {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 1;
  color: var(--gold-ink);
}

.card-score.is-negative {
  color: var(--card-red);
}

.finale-caption {
  font-weight: 600;
  font-size: var(--step-2);
  line-height: 1.1;
  text-align: center;
  overflow-wrap: anywhere;
}

.others {
  list-style: none;
  width: 100%;
  max-width: 26rem;
  margin-top: 1.25rem;
}

.other {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  min-height: 2.5rem;
  padding: 0.5rem 0;
}

.other-rank {
  flex: none;
  width: 1.125rem;
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.125rem;
  color: var(--on-table-muted);
}

.other-name {
  flex: 1;
  min-width: 0;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.other-gap {
  flex: none;
  font-size: var(--step--1);
  color: var(--on-table-muted);
}

.other-total {
  flex: none;
  min-width: 3.5rem;
  text-align: right;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.375rem;
  line-height: 1;
}

.finale-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  margin-top: 1.5rem;
}

.finale-actions .btn {
  flex: 1 1 13rem;
  max-width: 20rem;
}

.undo-link {
  margin-top: 0.75rem;
  min-height: 44px;
  padding: 0 0.75rem;
  border-radius: var(--radius-control);
  color: var(--on-table-muted);
  font-size: var(--step--1);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.undo-link:hover:not(:disabled) {
  color: var(--on-table);
}

.is-dealing .deal {
  animation: deal 0.55s cubic-bezier(0.2, 0.8, 0.2, 1) var(--delay) both;
}

.is-dealing .playing-card {
  animation: flip 0.6s cubic-bezier(0.45, 0, 0.2, 1) calc(var(--delay) + 0.65s) both;
}

@keyframes deal {
  from {
    opacity: 0;
    transform: translateY(-3rem) rotate(calc(var(--tilt) - 14deg)) scale(0.92);
  }
  to {
    opacity: 1;
    transform: rotate(var(--tilt));
  }
}

@keyframes flip {
  from {
    transform: rotateY(180deg);
  }
  to {
    transform: rotateY(0);
  }
}

@media (max-width: 419px) {
  .finale {
    padding: 1.25rem 1rem 1rem;
  }

  .other-gap {
    display: none;
  }
}

@media (min-width: 1024px) {
  .finale {
    padding: 2rem 1.5rem 1.5rem;
  }

  .winners {
    --card-w: clamp(7.5rem, calc(36vw / var(--count)), 13.5rem);
  }

  .sheet-link {
    display: none;
  }
}
</style>
